import React, { useMemo, useState } from "react";
import { ENGLISH_ABBREV, LanguageCode, SPANISH_ABBREV } from "@bto/shared";
import { DetailedRequisitionSerializer } from "../../recoil/types";
import {
  RefreshRequisitionRows,
  ReleasePatientReport,
} from "../Grid/Services/GridService";
import {
  ResultInfoIconWrapper,
  ResultInfoIcon,
  ResultInfoContainer,
  ResultInfoTitle,
  ResultInfoDownloadWrapper,
  UserReportPatientReleaseIndicator,
  UserReportPatientReleaseStatusIndicator,
  UserReportPatientReleaseGrid,
} from "./UserReport.styles";
import { useRequisitionSetter } from "../../recoil/useRequisitionSetter";
import { getFinalState, getReleaseInfo } from "../../recoil/helpers";
import { useAuthenticationHook } from "../../utils/hooks/useAuthenticationHook";
import {
  DownloadDropdown,
  DownloadLanguageItem,
} from "../shared/DownloadDropdown/DownloadDropdown";
import { DownloadIcon } from "../../assets/iconComponents/DownloadIcon";
import {
  useReportDownload,
  getAvailableLanguages,
} from "../../utils/hooks/useReportDownload";
import { getIcon } from "../Grid/ReportStatusIcon";
import { StatusIconSlot } from "../shared/StatusIconSlot.styles";

// Per-language UI copy for the download dropdown. Add new entries here when a
// new summary language is added on the backend.
const LANGUAGE_LABELS: Record<string, string> = {
  [ENGLISH_ABBREV]: "Download report in English",
  [SPANISH_ABBREV]: "Download summary in Spanish",
};

const DISABLED_LANGUAGE_TOOLTIPS: Record<string, string> = {
  [SPANISH_ABBREV]:
    "Spanish summaries are only available for negative carrier and low-risk aneuploidy results.",
};

const ResultsInfo: React.FC<{
  value: string;
  title: string;
  data: DetailedRequisitionSerializer;
  carrier: boolean;
  aneuploidy: boolean;
}> = ({ value = "", data, carrier, aneuploidy, title }) => {
  const { auth, setLoggedOut } = useAuthenticationHook();
  const { setRequisitions, searchText } = useRequisitionSetter();
  const [downloaded, setDownloaded] = useState(false);
  const { downloadReport, isDownloading, isDownloadingSpanish } =
    useReportDownload();

  const isCarrierValid =
    carrier && value !== "Not Ordered" && value !== "Processing";
  const isAneuploidyValid =
    aneuploidy && value !== "Not Ordered" && value !== "Processing";

  const isLims = carrier
    ? data?.unity_report?.is_lims
    : (data?.aneuploidy_report?.is_lims ?? true);

  const isCarrierRead = isCarrierValid && data?.unity_report?.is_read;
  const isAneuploidyRead =
    isAneuploidyValid && data?.aneuploidy_report?.is_read;

  const [carrierPatientRelease, setCarrierPatientRelease] = useState(
    isCarrierValid && data?.unity_report?.patient_release_state,
  );
  const [aneuploidyPatientRelease, setAneuploidyPatientRelease] = useState(
    isAneuploidyValid && data?.aneuploidy_report?.patient_release_state,
  );

  const reportId =
    (isCarrierValid
      ? data.unity_report?.report_pk
      : data.aneuploidy_report?.report_pk) || "";
  const reportType = isCarrierValid ? "carrier" : "aneuploidy";
  const pdfUrlsByLanguage = isCarrierValid
    ? data.unity_report?.pdf_urls_by_language
    : data.aneuploidy_report?.pdf_urls_by_language;
  const enabledLanguages = data.enabled_summary_languages;
  const availableLanguages = useMemo(
    () => getAvailableLanguages(pdfUrlsByLanguage),
    [pdfUrlsByLanguage],
  );

  const handleDownload = async (language?: LanguageCode) => {
    const downloadSucceeded = await downloadReport(
      reportId,
      reportType,
      "report_screen",
      language,
      availableLanguages,
    );

    if (!downloadSucceeded) return;

    setDownloaded(true);
    await RefreshRequisitionRows(
      auth!.token,
      setRequisitions,
      setLoggedOut,
      searchText,
    );
  };

  // Build the per-language items for the dropdown. Non-English entries appear
  // only when the requisition's account has the corresponding language
  // preference enabled; if a Spanish PDF wasn't generated for this report
  // (i.e. not Negative carrier or Low Risk aneuploidy), the entry renders
  // as disabled with a tooltip explaining why.
  const dropdownItems: DownloadLanguageItem[] = enabledLanguages.map((lang) => {
    const isEnglish = lang === ENGLISH_ABBREV;
    const hasPdf = isEnglish || !!pdfUrlsByLanguage?.[lang];
    return {
      language: lang as LanguageCode,
      label: LANGUAGE_LABELS[lang] ?? `Download report in ${lang}`,
      isDownloading: isEnglish ? isDownloading : isDownloadingSpanish,
      isDisabled: !hasPdf,
      disabledTooltip: !hasPdf ? DISABLED_LANGUAGE_TOOLTIPS[lang] : undefined,
      onDownload: () =>
        handleDownload(isEnglish ? undefined : (lang as LanguageCode)),
    };
  });

  const showDropdown = enabledLanguages.length > 1;

  const { icon, styles } = getIcon(value);

  return (
    <ResultInfoContainer>
      <ResultInfoTitle>{title}</ResultInfoTitle>
      <ResultInfoIconWrapper downloadIcon={isCarrierValid || isAneuploidyValid}>
        <ResultInfoIcon>
          {icon && <StatusIconSlot>{icon}</StatusIconSlot>}
          <span style={styles}>{value}</span>
        </ResultInfoIcon>
        {(isCarrierValid || isAneuploidyValid) &&
          !isLims &&
          (showDropdown ? (
            <DownloadDropdown
              isRead={!!(isCarrierRead || isAneuploidyRead)}
              downloaded={downloaded}
              items={dropdownItems}
            />
          ) : (
            <ResultInfoDownloadWrapper onClick={() => handleDownload()}>
              <DownloadIcon
                color={
                  isCarrierRead || isAneuploidyRead || downloaded
                    ? "lightgray"
                    : undefined
                }
              />
            </ResultInfoDownloadWrapper>
          ))}
        {(isCarrierValid || isAneuploidyValid) && isLims && (
          <ResultInfoDownloadWrapper>
            <DownloadIcon
              title="This report is unavailable to download - please call customer service."
              color="#FFB6B3"
            />
          </ResultInfoDownloadWrapper>
        )}
        {auth?.release_to_patient_enabled && (
          <UserReportPatientReleaseGrid>
            {carrierPatientRelease &&
              (carrierPatientRelease === "releasable" ? (
                <UserReportPatientReleaseIndicator
                  button
                  onClick={async () => {
                    try {
                      if (!data.unity_report.report_pk) return;
                      setCarrierPatientRelease("releasing");
                      const response = await ReleasePatientReport(
                        auth!.token,
                        true,
                        data.unity_report.report_pk,
                        setLoggedOut,
                      );
                      if (response?.ok) {
                        const responseData =
                          (await response.json()) as DetailedRequisitionSerializer;
                        setCarrierPatientRelease(
                          getFinalState(
                            responseData.unity_report?.patient_release_state ||
                              "unreleased",
                          ),
                        );
                        await RefreshRequisitionRows(
                          auth!.token,
                          setRequisitions,
                          setLoggedOut,
                          searchText,
                        );
                      }
                    } catch (e) {
                      console.error("Failed to release carrier report:", e);
                    }
                  }}
                >
                  Release Report
                </UserReportPatientReleaseIndicator>
              ) : (
                <UserReportPatientReleaseStatusIndicator>
                  {getReleaseInfo(carrierPatientRelease)}
                </UserReportPatientReleaseStatusIndicator>
              ))}

            {aneuploidyPatientRelease &&
              (aneuploidyPatientRelease === "releasable" ? (
                <UserReportPatientReleaseIndicator
                  button
                  onClick={async () => {
                    try {
                      if (!data.aneuploidy_report.report_pk) return;
                      setAneuploidyPatientRelease("releasing");
                      const response = await ReleasePatientReport(
                        auth!.token,
                        false,
                        data.aneuploidy_report.report_pk,
                        setLoggedOut,
                      );
                      if (response?.ok) {
                        const responseData =
                          (await response.json()) as DetailedRequisitionSerializer;
                        setAneuploidyPatientRelease(
                          getFinalState(
                            responseData.aneuploidy_report
                              ?.patient_release_state || "unreleased",
                          ),
                        );
                        await RefreshRequisitionRows(
                          auth!.token,
                          setRequisitions,
                          setLoggedOut,
                          searchText,
                        );
                      }
                    } catch (e) {
                      console.error("Failed to release aneuploidy report:", e);
                    }
                  }}
                >
                  Release Report
                </UserReportPatientReleaseIndicator>
              ) : (
                <UserReportPatientReleaseStatusIndicator>
                  {getReleaseInfo(aneuploidyPatientRelease)}
                </UserReportPatientReleaseStatusIndicator>
              ))}
          </UserReportPatientReleaseGrid>
        )}
      </ResultInfoIconWrapper>
    </ResultInfoContainer>
  );
};

export default ResultsInfo;
